import React from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts'
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material'
import { ChartData } from '../../types'
import { useContainerDimensions } from '../../hooks/useContainerDimensions'
import { useThemeColors } from '../../hooks/useThemeColors'

interface BarChartComponentProps {
  data: ChartData[]
  loading?: boolean
  error?: string | null
}

const BarChartComponent: React.FC<BarChartComponentProps> = ({
  data = [],
  loading = false,
  error = null
}) => {
  const [containerRef, dimensions] = useContainerDimensions()
  const colors = useThemeColors()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Typography
        variant='h5'
        gutterBottom
        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
        Monthly Revenue
      </Typography>
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, sm: 3 },
          backgroundColor: colors.background.paper,
          width: '100%',
          height: isMobile ? 350 : 400,
          display: 'flex',
          flexDirection: 'column'
        }}>
        <Box
          ref={containerRef}
          sx={{
            width: '100%',
            height: '100%',
            flex: 1,
            minHeight: 0,
            position: 'relative'
          }}>
          {loading && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: colors.text.secondary
              }}>
              <CircularProgress />
              <Typography variant='body1' sx={{ mt: 2 }}>
                Loading chart data...
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity='error'>Failed to load chart data: {error}</Alert>
          )}

          {!loading && !error && (
            <BarChart
              width={dimensions.width}
              height={dimensions.height}
              data={data}
              margin={{
                top: 20,
                right: isMobile ? 10 : 30,
                left: isMobile ? 0 : 20,
                bottom: 5
              }}>
              <CartesianGrid strokeDasharray='3 3' />
              <XAxis
                dataKey='name'
                tick={{
                  fill: colors.text.primary,
                  fontSize: isMobile ? 12 : 14
                }}
              />
              <YAxis
                tick={{
                  fill: colors.text.primary,
                  fontSize: isMobile ? 12 : 14
                }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: colors.background.paper,
                  borderColor: colors.divider,
                  color: colors.text.primary,
                  fontSize: isMobile ? 12 : 14
                }}
              />
              <Legend
                wrapperStyle={{
                  fontSize: isMobile ? '12px' : '14px'
                }}
              />
              <Bar
                dataKey='value'
                name='Revenue ($)'
                fill={colors.primary}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          )}
        </Box>
      </Paper>
    </Box>
  )
}

export default BarChartComponent
